import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Merge, RefreshCw, AlertTriangle } from "lucide-react";

/**
 * CustomersDedupe — Detecta clientes duplicados por nombre normalizado + caja,
 * y permite fusionarlos en un solo registro (master), migrando deudas, ventas,
 * notas de crédito y pagos.
 */
export default function CustomersDedupe() {
  const { selectedBox } = useBox() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // groupIdx -> {master_id, duplicate_ids[]}

  const fetch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBox?.id) params.set("box_id", selectedBox.id);
      const r = await axiosInstance.get(`/customers/dedupe/preview?${params.toString()}`);
      setData(r.data);
      setSelected({});
    } catch (err) {
      toast.error("No se pudo cargar duplicados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, [selectedBox?.id]);

  const toggleMaster = (gi, cid) => {
    setSelected((prev) => {
      const cur = prev[gi] || { master_id: null, duplicate_ids: [] };
      return {
        ...prev,
        [gi]: { ...cur, master_id: cur.master_id === cid ? null : cid, duplicate_ids: cur.duplicate_ids.filter(x => x !== cid) },
      };
    });
  };

  const toggleDup = (gi, cid) => {
    setSelected((prev) => {
      const cur = prev[gi] || { master_id: null, duplicate_ids: [] };
      if (cur.master_id === cid) return prev;
      const has = cur.duplicate_ids.includes(cid);
      return {
        ...prev,
        [gi]: { ...cur, duplicate_ids: has ? cur.duplicate_ids.filter(x => x !== cid) : [...cur.duplicate_ids, cid] },
      };
    });
  };

  const mergeGroup = async (gi) => {
    const sel = selected[gi];
    if (!sel?.master_id || !sel?.duplicate_ids?.length) {
      toast.error("Selecciona 1 registro como principal y marca los duplicados a fusionar");
      return;
    }
    if (!window.confirm(`¿Fusionar ${sel.duplicate_ids.length} cliente(s) en el principal? Esta acción NO se puede deshacer.`)) return;
    try {
      const r = await axiosInstance.post(`/customers/dedupe/merge`, sel);
      const u = r.data.updates;
      toast.success(`Fusionados ${u.deleted} clientes. Deudas: ${u.debts}, Ventas: ${u.sales}, Pagos: ${u.payments}`);
      await fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al fusionar");
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-4" data-testid="customers-dedupe">
      <div className="flex justify-between items-start flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Merge className="w-6 h-6 text-blue-600" />
            Deduplicación de Clientes
          </h1>
          <p className="text-sm text-slate-600">Fusiona clientes duplicados manteniendo todo su historial</p>
        </div>
        <Button variant="outline" onClick={fetch} disabled={loading} data-testid="dedupe-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refrescar
        </Button>
      </div>

      <Card className="border-amber-200 bg-amber-50/50">
        <CardContent className="pt-3 text-sm text-amber-900 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            La agrupación se hace por <b>nombre normalizado</b> (case-insensitive, sin puntuación) dentro de la misma caja. Marca <b>uno como Principal</b> (verde) y los demás como <b>Duplicados</b> (rojo). Las deudas, ventas, notas de crédito y pagos se migrarán al principal.
          </div>
        </CardContent>
      </Card>

      {loading && <p className="text-center text-slate-500 py-8">Buscando duplicados...</p>}
      {!loading && data && data.count === 0 && (
        <Card><CardContent className="pt-6 text-center text-emerald-700 font-semibold">
          <Users className="w-8 h-8 mx-auto mb-2" />
          🎉 No se detectaron duplicados en esta caja
        </CardContent></Card>
      )}

      {!loading && data && data.count > 0 && (
        <>
          <p className="text-sm text-slate-600">{data.count} grupo(s) de duplicados encontrados</p>
          <div className="space-y-3">
            {data.groups.map((g, gi) => {
              const sel = selected[gi] || { master_id: null, duplicate_ids: [] };
              return (
                <Card key={gi} data-testid={`dedupe-group-${gi}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex justify-between items-center">
                      <span>👥 {g.normalized_name.toUpperCase()} ({g.count} registros)</span>
                      <Button
                        onClick={() => mergeGroup(gi)}
                        disabled={!sel.master_id || sel.duplicate_ids.length === 0}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                        data-testid={`dedupe-merge-${gi}`}
                      >
                        <Merge className="w-4 h-4 mr-1" />
                        Fusionar {sel.duplicate_ids.length > 0 ? `(${sel.duplicate_ids.length})` : ""}
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-2">
                      {g.customers.map((c) => {
                        const isMaster = sel.master_id === c.id;
                        const isDup = sel.duplicate_ids.includes(c.id);
                        return (
                          <div key={c.id} className={`rounded border p-2 flex justify-between items-center gap-3 ${isMaster ? "border-emerald-400 bg-emerald-50" : isDup ? "border-red-400 bg-red-50" : "border-slate-200"}`}>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold">{c.name}</div>
                              <div className="text-xs text-slate-600">
                                Cédula: {c.cedula || "—"} · Tel: {c.phone || "—"} · Deudas: <b>{c.debts_count}</b> · Ventas: <b>{c.sales_count}</b>
                              </div>
                              <div className="text-[10px] text-slate-400">ID: {c.id.slice(0, 8)}... · Creado: {(c.created_at || "").slice(0, 10)}</div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button
                                size="sm"
                                variant={isMaster ? "default" : "outline"}
                                onClick={() => toggleMaster(gi, c.id)}
                                className={isMaster ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                data-testid={`dedupe-master-${c.id}`}
                              >
                                {isMaster ? "★ Principal" : "Elegir"}
                              </Button>
                              <Button
                                size="sm"
                                variant={isDup ? "default" : "outline"}
                                onClick={() => toggleDup(gi, c.id)}
                                disabled={isMaster}
                                className={isDup ? "bg-red-500 hover:bg-red-600 text-white" : ""}
                                data-testid={`dedupe-dup-${c.id}`}
                              >
                                {isDup ? "✕ Duplicado" : "Duplicar"}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
